import React from 'react';
import { bgColorClassname } from '@/utils';
import { LinkInterface } from '@/types';
import { Heading } from '@/components';

interface Items {
  title: string;
  text?: string;
}

interface ListItem {
  title?: string;
  items: Items[];
}

interface ListsProps {
  backgroundColor?: {
    label: string;
    value: string;
  };
  title: string;
  includeCta: boolean;
  cta?: LinkInterface;
  blurb?: string;
  isCompact: boolean;
  listItems: ListItem[];
}

// Utility function to determine the SVG fill color based on the background color
const getSvgColor = (bgColor: string, originalColor: string): string => {
  const colorMappings: { [key: string]: { [key: string]: string } } = {
    '#C9EDF3': { '#87D7E4': '#FC9997' },
    '#ACE4EC': { '#87D7E4': '#FC9997' },
    '#87D7E4': { '#87D7E4': '#FC9997' },
    '#61CADB': { '#87D7E4': '#FC9997' },
    '#3986C7': { '#3986C7': '#FC9997' },
    '#22426A': { '#3986C7': '#FC9997' },
    '#A0DAB3': { '#86D09F': '#FC9997' },
    '#FFE23D': { '#FFE23D': '#FC9997' },
    '#FFD700': { '#FFE23D': '#FC9997' },
    '#FC9997': { '#FC9997': '#87D7E4' },
  };

  return colorMappings[bgColor]?.[originalColor] || originalColor;
};

const Lists: React.FC<ListsProps> = ({
  backgroundColor,
  title,
  includeCta,
  cta,
  blurb,
  isCompact,
  listItems,
}) => {
  const bgColor = backgroundColor ? backgroundColor.value : '';

  const svgs = [
    <svg
      width='42'
      height='41'
      viewBox='0 0 42 41'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
    >
      <path
        d='M34.8842 20.147C37.6159 20.147 39.5987 22.7153 38.8347 25.2862C36.5679 32.9166 29.3797 38.4923 20.8647 38.4923C12.3496 38.4923 5.16146 32.9166 2.89468 25.2862C2.13072 22.7153 4.11351 20.147 6.8452 20.147H34.885H34.8842Z'
        fill={getSvgColor(bgColor, '#3986C7')}
      />
      <path
        d='M35.1547 2C37.8864 2 39.8692 4.56834 39.1052 7.13919C36.8384 14.7697 29.6503 20.3453 21.1352 20.3453C12.6201 20.3453 5.43196 14.7697 3.16519 7.13919C2.40122 4.56834 4.38402 2 7.11571 2H35.1555H35.1547Z'
        fill={getSvgColor(bgColor, '#3986C7')}
      />
    </svg>,
    <svg
      width='42'
      height='41'
      viewBox='0 0 42 41'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
    >
      <path
        d='M22.703 1.63387L23.4573 4.93981C24.1636 8.03129 27.892 9.50203 30.6865 7.79105L33.6754 5.96171C35.2504 4.99723 37.0962 6.75509 36.0835 8.25513L34.1626 11.1017C32.3661 13.7631 33.9104 17.3139 37.1564 17.9866L40.6277 18.705C42.4574 19.0835 42.4574 21.5703 40.6277 21.9488L37.1564 22.6672C33.9104 23.3399 32.3661 26.8907 34.1626 29.5521L36.0835 32.3987C37.0962 33.8987 35.2504 35.6566 33.6754 34.6921L30.6865 32.8628C27.892 31.1518 24.1636 32.6225 23.4573 35.714L22.703 39.0199C22.3056 40.7626 19.6944 40.7626 19.297 39.0199L18.5427 35.714C17.8364 32.6225 14.108 31.1518 11.3135 32.8628L8.32463 34.6921C6.74959 35.6566 4.90384 33.8987 5.91654 32.3987L7.83735 29.5521C9.63388 26.8907 8.08961 23.3399 4.84355 22.6672L1.37231 21.9488C-0.457438 21.5703 -0.457438 19.0835 1.37231 18.705L4.84355 17.9866C8.08961 17.3139 9.63388 13.7631 7.83735 11.1017L5.91654 8.25513C4.90384 6.75509 6.74959 4.99723 8.32463 5.96171L11.3135 7.79105C14.108 9.50203 17.8364 8.03129 18.5427 4.93981L19.297 1.63387C19.6944 -0.108751 22.3056 -0.108751 22.703 1.63387Z'
        fill={getSvgColor(bgColor, '#87D7E4')}
      />
    </svg>,
    <svg
      width='42'
      height='46'
      viewBox='0 0 42 46'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
    >
      <path
        d='M32 3.71687V14.8675C32 16.9204 30.3574 18.5844 28.3307 18.5844H25.7927C23.4089 18.5844 21.4017 20.4849 21.3479 22.8984C21.2929 25.38 23.2694 27.4156 25.7059 27.4156H28.3307C30.3574 27.4156 32 29.0796 32 31.1325V42.2831C32 44.3361 30.3574 46 28.3307 46H25.9077C13.4221 46 3.00367 35.6535 3 23.0062C2.99633 10.3205 13.1836 1.26802e-07 25.7059 1.26802e-07H28.3307C30.3574 1.26802e-07 32 1.66392 32 3.71687Z'
        fill={getSvgColor(bgColor, '#86D09F')}
      />
    </svg>,
    <svg
      width='42'
      height='40'
      viewBox='0 0 42 40'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
    >
      <path
        d='M38.0331 12.9446C38.0589 10.3827 37.0968 7.81217 35.1423 5.85769C33.1878 3.90321 30.6173 2.94105 28.0554 2.9669C26.2632 1.13736 23.7644 0 21 0C18.2356 0 15.7383 1.13736 13.9446 2.9669C11.3827 2.94105 8.81216 3.90321 6.85768 5.85769C4.90321 7.81217 3.94105 10.3827 3.9669 12.9446C2.13736 14.7368 1 17.2356 1 20C1 22.7644 2.13736 25.2617 3.9669 27.0554C3.94105 29.6173 4.90321 32.1878 6.85768 34.1423C8.81216 36.0968 11.3827 37.0589 13.9446 37.0331C15.7368 38.8626 18.2356 40 21 40C23.7644 40 26.2617 38.8626 28.0554 37.0331C30.6173 37.0589 33.1878 36.0968 35.1423 34.1423C37.0968 32.1878 38.0589 29.6173 38.0331 27.0554C39.8626 25.2632 41 22.7644 41 20C41 17.2356 39.8626 14.7383 38.0331 12.9446Z'
        fill={getSvgColor(bgColor, '#FFE23D')}
      />
    </svg>,
    <svg
      width='42'
      height='40'
      viewBox='0 0 42 40'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
    >
      <path
        d='M39 8.67209C39 5.53978 36.3549 3 33.0927 3H8.9073C5.64509 3 3 5.53978 3 8.67209C3 11.657 5.40211 14.1042 8.45142 14.3279C5.40211 14.5516 3 16.998 3 19.9837C3 23.116 5.64509 25.6558 8.9073 25.6558C5.64509 25.6558 3 28.1956 3 31.3279C3 34.4602 5.64509 37 8.9073 37H33.0927C36.3549 37 39 34.4602 39 31.3279C39 28.1956 36.3549 25.6558 33.0927 25.6558C36.3549 25.6558 39 23.116 39 19.9837C39 16.9988 36.5979 14.5516 33.5486 14.3279C36.5979 14.1042 39 11.6578 39 8.67209Z'
        fill={getSvgColor(bgColor, '#F05340')}
      />
    </svg>,
  ];

  const CompactSvgs = [
    <svg
      width='25'
      height='24'
      viewBox='0 0 25 24'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
    >
      <path
        d='M22.1632 11.9673C24.046 11.9673 25.4127 13.6518 24.8861 15.3381C23.3237 20.3428 18.3692 23.9998 12.5 23.9998C6.63085 23.9998 1.67627 20.3428 0.113861 15.3381C-0.412717 13.6518 0.95396 11.9673 2.83682 11.9673H22.1638H22.1632Z'
        fill={getSvgColor(bgColor, '#3986C7')}
      />
      <path
        d='M22.1632 0C24.046 0 25.4127 1.68456 24.8861 3.37076C23.3237 8.37554 18.3692 12.0326 12.5 12.0326C6.63085 12.0326 1.67627 8.37554 0.113861 3.37076C-0.412717 1.68456 0.953963 0 2.83683 0H22.1638H22.1632Z'
        fill={getSvgColor(bgColor, '#3986C7')}
      />
    </svg>,
    <svg
      width='25'
      height='24'
      viewBox='0 0 25 24'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
    >
      <path
        d='M13.5137 0.784179L13.9627 2.76774C14.3831 4.62263 16.6024 5.50507 18.2658 4.47849L20.0449 3.38088C20.9824 2.8022 22.081 3.85691 21.4782 4.75693L20.3349 6.46486C19.2655 8.0617 20.1848 10.1922 22.1169 10.5958L24.1831 11.0268C25.2723 11.254 25.2723 12.746 24.1831 12.9731L22.1169 13.4042C20.1848 13.8078 19.2655 15.9383 20.3349 17.5351L21.4782 19.2431C22.081 20.1431 20.9824 21.1978 20.0449 20.6191L18.2658 19.5215C16.6024 18.4949 14.3831 19.3774 13.9627 21.2323L13.5137 23.2158C13.2771 24.2614 11.7229 24.2614 11.4863 23.2158L11.0373 21.2323C10.6169 19.3774 8.3976 18.4949 6.73423 19.5215L4.95514 20.6191C4.01761 21.1978 2.91896 20.1431 3.52175 19.2431L4.66509 17.5351C5.73445 15.9383 4.81524 13.8078 2.88307 13.4042L0.816853 12.9731C-0.272284 12.746 -0.272284 11.254 0.816853 11.0268L2.88307 10.5958C4.81524 10.1922 5.73445 8.0617 4.66509 6.46486L3.52175 4.75693C2.91896 3.85691 4.01761 2.8022 4.95514 3.38088L6.73423 4.47849C8.3976 5.50507 10.6169 4.62263 11.0373 2.76774L11.4863 0.784179C11.7229 -0.261393 13.2771 -0.261393 13.5137 0.784179Z'
        fill={getSvgColor(bgColor, '#87D7E4')}
      />
    </svg>,
    <svg
      width='25'
      height='24'
      viewBox='0 0 25 24'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
    >
      <path
        d='M20.1304 1.93924V7.75695C20.1304 8.82805 19.2734 9.69619 18.216 9.69619H16.8918C15.6481 9.69619 14.6009 10.6878 14.5728 11.947C14.5441 13.2418 15.5753 14.3038 16.8465 14.3038H18.216C19.2734 14.3038 20.1304 15.1719 20.1304 16.243V22.0608C20.1304 23.1319 19.2734 24 18.216 24H16.9518C10.4376 24 5.00191 18.6018 5 12.0032C4.99809 5.38461 10.3132 -1.90735e-06 16.8465 -1.90735e-06H18.216C19.2734 -1.90735e-06 20.1304 0.86813 20.1304 1.93924Z'
        fill={getSvgColor(bgColor, '#86D09F')}
      />
    </svg>,
    <svg
      width='25'
      height='24'
      viewBox='0 0 25 24'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
    >
      <path
        d='M23.2199 7.76678C23.2354 6.22963 22.6581 4.6873 21.4854 3.51461C20.3127 2.34193 18.7704 1.76463 17.2332 1.78014C16.1579 0.682418 14.6586 0 13 0C11.3413 0 9.84296 0.682418 8.76678 1.78014C7.22962 1.76463 5.6873 2.34193 4.51461 3.51461C3.34192 4.6873 2.76463 6.22963 2.78014 7.76678C1.68241 8.84211 1 10.3414 1 12C1 13.6586 1.68241 15.157 2.78014 16.2332C2.76463 17.7704 3.34192 19.3127 4.51461 20.4854C5.6873 21.6581 7.22962 22.2354 8.76678 22.2199C9.8421 23.3176 11.3413 24 13 24C14.6586 24 16.157 23.3176 17.2332 22.2199C18.7704 22.2354 20.3127 21.6581 21.4854 20.4854C22.6581 19.3127 23.2354 17.7704 23.2199 16.2332C24.3176 15.1579 25 13.6586 25 12C25 10.3414 24.3176 8.84297 23.2199 7.76678Z'
        fill={getSvgColor(bgColor, '#FFE23D')}
      />
    </svg>,
    <svg
      width='25'
      height='24'
      viewBox='0 0 25 24'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
    >
      <path
        d='M25 4.00383C25 1.79278 23.1631 0 20.8977 0H4.10229C1.83687 0 0 1.79278 0 4.00383C0 6.11084 1.66813 7.83827 3.78571 7.99617C1.66813 8.15406 0 9.88097 0 11.9885C0 14.1995 1.83687 15.9923 4.10229 15.9923C1.83687 15.9923 0 17.7851 0 19.9962C0 22.2072 1.83687 24 4.10229 24H20.8977C23.1631 24 25 22.2072 25 19.9962C25 17.7851 23.1631 15.9923 20.8977 15.9923C23.1631 15.9923 25 14.1995 25 11.9885C25 9.88149 23.3319 8.15406 21.2143 7.99617C23.3319 7.83827 25 6.11136 25 4.00383Z'
        fill={getSvgColor(bgColor, '#F05340')}
      />
    </svg>,
  ];

  let globalIndex = 0;

  return (
    <div className={backgroundColor ? bgColorClassname(backgroundColor) : ''}>
      <div className='font-halyarddisplay mx-auto max-w-screen-xl px-5 py-8 md:p-[50px]'>
        <Heading
          title={title}
          isShowButton={includeCta}
          cta={cta}
        />
        {blurb && <p className='blurb mb-4 text-xl font-normal leading-7 text-black'>{blurb}</p>}
        <div className='lists-content-layer'>
          <div
            className={`lists block ${listItems.length > 1 ? 'sm:flex sm:gap-x-4 md:block lg:flex xl:gap-x-9' : ''}`}
          >
            {listItems.map((listItem, listIndex) => (
              <div
                key={listIndex}
                className={`list w-full ${listItems.length > 1 ? 'mb-8 sm:mb-0 sm:w-1/2 md:mb-8 md:w-full lg:mb-0 lg:w-1/2' : ''}`}
              >
                {listItem.title && (
                  <h3 className='mb-7 text-2xl font-bold text-black'>{listItem.title}</h3>
                )}
                <ul>
                  {listItem.items.map((item, itemIndex) => {
                    // Use the current value of globalIndex
                    const currentIndex = globalIndex;
                    const svgToRender = isCompact
                      ? CompactSvgs[currentIndex % 5]
                      : svgs[currentIndex % 5];

                    // Return the list item
                    const listItemElement = (
                      <li
                        className={`relative before:absolute before:bottom-[-5px] before:top-1.5 before:z-0 before:border-l before:border-black before:content-[""] last:pb-0 last:before:border-l-0 ${isCompact ? 'pb-4 before:left-3' : 'pb-7 before:left-[20px]'}`}
                        key={itemIndex}
                        data-bullet={currentIndex % 5}
                      >
                        <div className='relative z-[1] flex gap-x-3'>
                          <div className={`list-image-icon ${isCompact ? '' : 'w-11'}`}>
                            {svgToRender}
                          </div>
                          <div className=''>
                            {item.title && (
                              <h4
                                className={`mb-1 font-semibold text-black ${isCompact ? 'text-base leading-5' : 'text-xl leading-7'}`}
                              >
                                {item.title}
                              </h4>
                            )}
                            {item.text && (
                              <p
                                className={`whitespace-pre-line font-light text-black ${isCompact ? 'text-sm leading-5' : 'text-lg leading-7'}`}
                              >
                                {item.text}
                              </p>
                            )}
                          </div>
                        </div>
                      </li>
                    );

                    // Increment the global index after returning the list item
                    globalIndex++;
                    return listItemElement;
                  })}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Lists;
